import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import FintreeConfig from "@/app/config";

const axiosInstance: AxiosInstance = axios.create({
  withCredentials: true,
});

let refreshPromise: Promise<void> | null = null;

async function refreshToken(): Promise<void> {
  await axios.post(
    `${FintreeConfig.baseAPIEndpoint}/v1/auth/refresh`,
    {},
    { withCredentials: true },
  );

  window.dispatchEvent(new Event("auth-refreshed"));
}

function getOrCreateRefreshPromise(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = refreshToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

axiosInstance.interceptors.request.use(
  (config) => {
    if (config.headers) {
      config.headers["Request-Start-Time"] = Date.now();
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

    if (!originalRequest) {
      return Promise.reject(error);
    }

    const isAuthRoute = originalRequest.url?.includes("/v1/auth/");

    if (error.response?.status !== 401 || originalRequest._retried || isAuthRoute) {
      return Promise.reject(error);
    }

    originalRequest._retried = true;

    try {
      await getOrCreateRefreshPromise();
      return axiosInstance(originalRequest);
    } catch {
      window.dispatchEvent(new Event("auth-logout"));
      return Promise.reject(error);
    }
  },
);

export default axiosInstance;
